import { useState, useEffect, useCallback } from 'react';
import { supabase, CmsCase } from '@/lib/supabase';
import RichEditor from '@/components/feature/RichEditor';
import ImageUploader from '@/components/base/ImageUploader';

interface ChallengeItem { title: string; desc: string; icon: string; }
interface SolutionItem { title: string; desc: string; icon: string; }
interface ResultItem { label: string; value: string; unit: string; icon: string; }
interface TestimonialData { content: string; author: string; position: string; }

interface EditForm {
  title: string;
  client: string;
  industry: string;
  summary: string;
  cover_image: string;
  overview: string;
  metric_value: string;
  metric_label: string;
  challenges: ChallengeItem[];
  solutions: SolutionItem[];
  results: ResultItem[];
  testimonial: TestimonialData;
}

type EditTab = 'basic' | 'challenges' | 'solutions' | 'results' | 'testimonial';

const DEFAULT_CHALLENGE: ChallengeItem = { title: '', desc: '', icon: 'ri-alert-line' };
const DEFAULT_SOLUTION: SolutionItem = { title: '', desc: '', icon: 'ri-lightbulb-line' };
const DEFAULT_RESULT: ResultItem = { label: '', value: '', unit: '%', icon: 'ri-bar-chart-line' };

export default function CasesCMS() {
  const [list, setList] = useState<CmsCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editData, setEditData] = useState<EditForm | null>(null);
  const [activeTab, setActiveTab] = useState<EditTab>('basic');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast(msg); setToastType(type); setTimeout(() => setToast(''), 3000);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('cms_cases').select('*').order('sort_order', { ascending: true });
      if (error) throw error;
      setList(data || []);
    } catch { showToast('加载失败', 'error'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleEdit = (item: CmsCase) => {
    setEditId(item.id);
    setActiveTab('basic');
    setEditData({
      title: item.title,
      client: item.client || '',
      industry: item.industry || '',
      summary: item.summary || '',
      cover_image: item.cover_image || '',
      overview: item.overview || '',
      metric_value: item.metric_value || '',
      metric_label: item.metric_label || '',
      challenges: (item.challenges as ChallengeItem[]) || [{ ...DEFAULT_CHALLENGE }],
      solutions: (item.solutions as SolutionItem[]) || [{ ...DEFAULT_SOLUTION }],
      results: (item.results as ResultItem[]) || [{ ...DEFAULT_RESULT }],
      testimonial: (item.testimonial as TestimonialData) || { content: '', author: '', position: '' },
    });
  };

  const handleSave = async () => {
    if (!editId || !editData) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('cms_cases').update({
        title: editData.title,
        client: editData.client,
        industry: editData.industry,
        summary: editData.summary,
        cover_image: editData.cover_image,
        overview: editData.overview,
        metric_value: editData.metric_value,
        metric_label: editData.metric_label,
        challenges: editData.challenges,
        solutions: editData.solutions,
        results: editData.results,
        testimonial: editData.testimonial,
        updated_at: new Date().toISOString(),
      }).eq('id', editId);
      if (error) throw error;
      showToast('保存成功'); setEditId(null); setEditData(null); await fetchList();
    } catch { showToast('保存失败', 'error'); } finally { setSaving(false); }
  };

  const handleMoveOrder = async (item: CmsCase, direction: 'up' | 'down') => {
    const idx = list.findIndex((c) => c.id === item.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    const swapItem = list[swapIdx];
    try {
      await Promise.all([
        supabase.from('cms_cases').update({ sort_order: swapItem.sort_order }).eq('id', item.id),
        supabase.from('cms_cases').update({ sort_order: item.sort_order }).eq('id', swapItem.id),
      ]);
      await fetchList();
    } catch { showToast('排序失败', 'error'); }
  };

  const handleTogglePublish = async (item: CmsCase) => {
    try {
      const { error } = await supabase.from('cms_cases').update({ is_published: !item.is_published, updated_at: new Date().toISOString() }).eq('id', item.id);
      if (error) throw error;
      showToast(item.is_published ? '已取消发布' : '已发布'); await fetchList();
    } catch { showToast('操作失败', 'error'); }
  };

  // ---- 列表项增删改 helpers ----
  const updateChallenge = (idx: number, field: keyof ChallengeItem, val: string) => {
    setEditData((d) => {
      if (!d) return d;
      const arr = [...d.challenges];
      arr[idx] = { ...arr[idx], [field]: val };
      return { ...d, challenges: arr };
    });
  };
  const addChallenge = () => setEditData((d) => d ? { ...d, challenges: [...d.challenges, { ...DEFAULT_CHALLENGE }] } : d);
  const removeChallenge = (idx: number) => setEditData((d) => d ? { ...d, challenges: d.challenges.filter((_, i) => i !== idx) } : d);

  const updateSolution = (idx: number, field: keyof SolutionItem, val: string) => {
    setEditData((d) => {
      if (!d) return d;
      const arr = [...d.solutions];
      arr[idx] = { ...arr[idx], [field]: val };
      return { ...d, solutions: arr };
    });
  };
  const addSolution = () => setEditData((d) => d ? { ...d, solutions: [...d.solutions, { ...DEFAULT_SOLUTION }] } : d);
  const removeSolution = (idx: number) => setEditData((d) => d ? { ...d, solutions: d.solutions.filter((_, i) => i !== idx) } : d);

  const updateResult = (idx: number, field: keyof ResultItem, val: string) => {
    setEditData((d) => {
      if (!d) return d;
      const arr = [...d.results];
      arr[idx] = { ...arr[idx], [field]: val };
      return { ...d, results: arr };
    });
  };
  const addResult = () => setEditData((d) => d ? { ...d, results: [...d.results, { ...DEFAULT_RESULT }] } : d);
  const removeResult = (idx: number) => setEditData((d) => d ? { ...d, results: d.results.filter((_, i) => i !== idx) } : d);

  const TABS: { key: EditTab; label: string; icon: string }[] = [
    { key: 'basic', label: '基本信息', icon: 'ri-file-info-line' },
    { key: 'challenges', label: '面临挑战', icon: 'ri-alert-line' },
    { key: 'solutions', label: '解决方案', icon: 'ri-lightbulb-line' },
    { key: 'results', label: '项目成果', icon: 'ri-bar-chart-line' },
    { key: 'testimonial', label: '客户评价', icon: 'ri-chat-quote-line' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800">客户案例管理</h2>
          <p className="text-sm text-gray-400 mt-0.5">共 {list.length} 个案例 · {list.filter((c) => c.is_published).length} 个已发布</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
              <div className="h-16 bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {list.map((item, idx) => (
            <div key={item.id} className={`bg-white rounded-xl border transition-all ${item.is_published ? 'border-gray-100' : 'border-gray-200 opacity-70'}`}>
              {editId === item.id ? (
                <div className="p-5 space-y-4">
                  {/* Tab 导航 */}
                  <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1 overflow-x-auto">
                    {TABS.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                          activeTab === tab.key
                            ? 'bg-white text-teal-700 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <i className={`${tab.icon} text-sm`}></i>
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* 基本信息 */}
                  {activeTab === 'basic' && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">案例标题</label>
                          <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" value={editData?.title || ''} onChange={(e) => setEditData((d) => d ? { ...d, title: e.target.value } : d)} />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">客户名称</label>
                          <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" value={editData?.client || ''} onChange={(e) => setEditData((d) => d ? { ...d, client: e.target.value } : d)} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">行业</label>
                          <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" value={editData?.industry || ''} onChange={(e) => setEditData((d) => d ? { ...d, industry: e.target.value } : d)} />
                        </div>
                        <div className="col-span-2">
                          <ImageUploader
                            label="封面图片"
                            value={editData?.cover_image || ''}
                            onChange={(url) => setEditData((d) => d ? { ...d, cover_image: url } : d)}
                            placeholder="粘贴图片 URL，如 https://..."
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">核心指标值</label>
                          <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" value={editData?.metric_value || ''} onChange={(e) => setEditData((d) => d ? { ...d, metric_value: e.target.value } : d)} placeholder="如：+300%" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">指标说明</label>
                          <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" value={editData?.metric_label || ''} onChange={(e) => setEditData((d) => d ? { ...d, metric_label: e.target.value } : d)} placeholder="如：效率提升" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">摘要 <span className="text-gray-300">（纯文本，用于列表预览）</span></label>
                        <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 resize-none" value={editData?.summary || ''} onChange={(e) => setEditData((d) => d ? { ...d, summary: e.target.value } : d)} maxLength={500} />
                        <p className="text-xs text-gray-400 mt-0.5">{(editData?.summary || '').length}/500</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">项目概述 <span className="text-gray-300">（富文本，详情页展示）</span></label>
                        <RichEditor value={editData?.overview || ''} onChange={(val) => setEditData((d) => d ? { ...d, overview: val } : d)} placeholder="请输入项目背景、目标和整体概述..." minHeight={260} />
                      </div>
                    </div>
                  )}

                  {/* 面临挑战 */}
                  {activeTab === 'challenges' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">编辑客户面临的核心挑战与痛点，建议 2-4 条</p>
                        <button onClick={addChallenge} className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap">
                          <i className="ri-add-line"></i>添加挑战
                        </button>
                      </div>
                      {editData?.challenges.map((c, idx) => (
                        <div key={idx} className="border border-gray-100 rounded-xl p-4 space-y-3 bg-red-50/30">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-600">挑战 {idx + 1}</span>
                            {(editData?.challenges.length ?? 0) > 1 && (
                              <button onClick={() => removeChallenge(idx)} className="text-xs text-rose-400 hover:text-rose-600 cursor-pointer">
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">标题</label>
                              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white" value={c.title} onChange={(e) => updateChallenge(idx, 'title', e.target.value)} placeholder="挑战标题" />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">图标类名</label>
                              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white" value={c.icon} onChange={(e) => updateChallenge(idx, 'icon', e.target.value)} placeholder="ri-alert-line" />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">详细描述</label>
                            <RichEditor value={c.desc} onChange={(val) => updateChallenge(idx, 'desc', val)} placeholder="描述该挑战的具体表现和影响..." minHeight={160} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 解决方案 */}
                  {activeTab === 'solutions' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">编辑针对挑战提供的解决方案，建议 2-4 条</p>
                        <button onClick={addSolution} className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap">
                          <i className="ri-add-line"></i>添加方案
                        </button>
                      </div>
                      {editData?.solutions.map((s, idx) => (
                        <div key={idx} className="border border-gray-100 rounded-xl p-4 space-y-3 bg-teal-50/30">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-600">方案 {idx + 1}</span>
                            {(editData?.solutions.length ?? 0) > 1 && (
                              <button onClick={() => removeSolution(idx)} className="text-xs text-rose-400 hover:text-rose-600 cursor-pointer">
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">方案名称</label>
                              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white" value={s.title} onChange={(e) => updateSolution(idx, 'title', e.target.value)} placeholder="方案名称" />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">图标类名</label>
                              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white" value={s.icon} onChange={(e) => updateSolution(idx, 'icon', e.target.value)} placeholder="ri-lightbulb-line" />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">方案详情</label>
                            <RichEditor value={s.desc} onChange={(val) => updateSolution(idx, 'desc', val)} placeholder="详细描述该方案的技术实现和核心价值..." minHeight={160} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 项目成果 */}
                  {activeTab === 'results' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">编辑项目交付的量化成果指标，建议 3-4 条</p>
                        <button onClick={addResult} className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap">
                          <i className="ri-add-line"></i>添加指标
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        {editData?.results.map((r, idx) => (
                          <div key={idx} className="border border-gray-100 rounded-xl p-4 bg-emerald-50/30">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-xs font-medium text-gray-600">成果指标 {idx + 1}</span>
                              {(editData?.results.length ?? 0) > 1 && (
                                <button onClick={() => removeResult(idx)} className="text-xs text-rose-400 hover:text-rose-600 cursor-pointer">
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-4 gap-3">
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">指标名称</label>
                                <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white" value={r.label} onChange={(e) => updateResult(idx, 'label', e.target.value)} placeholder="如：效率提升" />
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">数值</label>
                                <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white" value={r.value} onChange={(e) => updateResult(idx, 'value', e.target.value)} placeholder="如：68" />
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">单位</label>
                                <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white" value={r.unit} onChange={(e) => updateResult(idx, 'unit', e.target.value)} placeholder="如：%" />
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">图标类名</label>
                                <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white" value={r.icon} onChange={(e) => updateResult(idx, 'icon', e.target.value)} placeholder="ri-bar-chart-line" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 客户评价 */}
                  {activeTab === 'testimonial' && (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500">编辑客户对项目的真实评价与反馈</p>
                      <div className="border border-gray-100 rounded-xl p-4 space-y-3 bg-amber-50/30">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">评价人姓名</label>
                            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white" value={editData?.testimonial.author || ''} onChange={(e) => setEditData((d) => d ? { ...d, testimonial: { ...d.testimonial, author: e.target.value } } : d)} placeholder="如：张建国" />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">职位</label>
                            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white" value={editData?.testimonial.position || ''} onChange={(e) => setEditData((d) => d ? { ...d, testimonial: { ...d.testimonial, position: e.target.value } } : d)} placeholder="如：某市信息化办公室主任" />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">评价内容</label>
                          <RichEditor value={editData?.testimonial.content || ''} onChange={(val) => setEditData((d) => d ? { ...d, testimonial: { ...d.testimonial, content: val } } : d)} placeholder="请输入客户的真实评价内容..." minHeight={200} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 操作按钮 */}
                  <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap disabled:opacity-50">
                      {saving ? '保存中...' : '保存所有更改'}
                    </button>
                    <button onClick={() => { setEditId(null); setEditData(null); }} className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap">
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-4 p-5">
                  {item.cover_image && (
                    <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                      <img src={item.cover_image} alt={item.title} className="w-full h-full object-cover object-top" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-800">{item.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${item.is_published ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {item.is_published ? '已发布' : '草稿'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {item.tags?.map((tag) => (
                        <span key={tag} className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded">{tag}</span>
                      ))}
                      <span className="text-xs text-gray-400">{item.client}</span>
                      {item.metric_value && (
                        <span className="text-xs font-bold text-teal-600">{item.metric_value} {item.metric_label}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      {item.challenges && <span className="text-xs text-gray-400"><i className="ri-alert-line mr-0.5"></i>{(item.challenges as ChallengeItem[]).length} 项挑战</span>}
                      {item.solutions && <span className="text-xs text-gray-400"><i className="ri-lightbulb-line mr-0.5"></i>{(item.solutions as SolutionItem[]).length} 项方案</span>}
                      {item.results && <span className="text-xs text-gray-400"><i className="ri-bar-chart-line mr-0.5"></i>{(item.results as ResultItem[]).length} 项成果</span>}
                      {item.testimonial && <span className="text-xs text-emerald-600"><i className="ri-chat-quote-line mr-0.5"></i>有客户评价</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleMoveOrder(item, 'up')}
                      disabled={idx === 0}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title="上移"
                    >
                      <i className="ri-arrow-up-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleMoveOrder(item, 'down')}
                      disabled={idx === list.length - 1}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title="下移"
                    >
                      <i className="ri-arrow-down-line text-base"></i>
                    </button>
                    <button onClick={() => handleEdit(item)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer" title="编辑">
                      <i className="ri-edit-line text-base"></i>
                    </button>
                    <button onClick={() => handleTogglePublish(item)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer" title={item.is_published ? '取消发布' : '发布'}>
                      <i className={`${item.is_published ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 text-white text-sm px-4 py-2.5 rounded-lg z-50 ${toastType === 'error' ? 'bg-rose-600' : 'bg-gray-800'}`}>
          <div className="flex items-center gap-2">
            <i className={`${toastType === 'error' ? 'ri-error-warning-line text-rose-200' : 'ri-checkbox-circle-line text-emerald-400'}`}></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
